import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { IonContent, IonPage, IonSpinner, useIonViewWillLeave } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import HeaderNav from '../../../../components/headerNav/HeaderNav.component'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { IOption, IOptions } from '../../../../common/common.types'
import { goBack } from '../../../../utilities/navigation.util'
import { reassignQaFlag, setReassignAssignee, setReassignReason } from '../store/qaAddendum.actions'
import { getQaAddendumSelector } from '../selectors'
import { ALL_CREW_MEMBERS_ID } from '../store/qaAddendum.constants'
import { canViewFlagSelector } from '../../../../features/user/selectors'
import { isFlagListPRIDBlackedOut, LockType } from '../../utils/qaFlagBlackout.util'
import ReassignCrewMemberContent from '../content/reassign/ReassignCrewMember.content'
import QaFlagActionFooter from '../../components/QaFlagActionFooter.component'
import QaFlagHeader from '../../components/QaFlagHeader.component'
import { useQAFlagNavigation } from '../../hooks/useQAFlagNavigation.hook'
import { useKeyboardOverlay } from '../../hooks/useKeyboardOverlay.hook'
import { OTHER_OBSERVER } from '../store/qaAddendum.constants'
import './FlagResolution.page.css'

const sameOptionId = (a: IOption, b: IOption): boolean => String(a.id) === String(b.id)

const ReassignPage: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { flagId, flagDetail, isDetailLoading } = useQAFlagNavigation()
  const { reassignAssignee: selectedCrewOption, reassignReason: reasonForChange } = useSelector(getQaAddendumSelector)
  const canViewFlag = useSelector(canViewFlagSelector)
  const isCrewNamesBlackedOut = isFlagListPRIDBlackedOut(flagDetail?.lockType ?? LockType.Normal, canViewFlag)

  const [isExpanded, setIsExpanded] = useState(false)
  useKeyboardOverlay()

  useIonViewWillLeave(() => {
    setIsExpanded(false)
  })

  const displayRelevantRole = (userRoles: IOptions | undefined) => {
    if (!userRoles || userRoles.length === 0) return OTHER_OBSERVER
    const allUserRoles = userRoles.map((role) => role?.name).filter((roleName) => roleName)
    if (allUserRoles.length === 0) return OTHER_OBSERVER
    return allUserRoles.join(', ')
  }

  const crewOptions: IOption[] = useMemo(() => {
    const options = flagDetail?.assignedToOptions ?? []
    if (isCrewNamesBlackedOut) {
      return options.map((opt) => ({
        id: opt.id,
        name: displayRelevantRole(opt.roles),
      }))
    }
    return options.map(({ id, name }) => ({ id, name }))
  }, [flagDetail?.assignedToOptions, isCrewNamesBlackedOut])
  const matchedIndex = selectedCrewOption
    ? crewOptions.findIndex((option) => sameOptionId(option, selectedCrewOption))
    : -1
  const selectedCrewIndex = matchedIndex >= 0 ? matchedIndex : null
  const isAllCrewSelected = selectedCrewOption != null && Number(selectedCrewOption.id) === ALL_CREW_MEMBERS_ID
  const canReassignDone = selectedCrewOption != null && reasonForChange.trim().length > 0

  useEffect(() => {
    if (selectedCrewOption || !flagDetail?.assignedTo) return
    const { assignedTo, assignedToOptions } = flagDetail
    const current = assignedToOptions?.find((option) => sameOptionId(option, assignedTo))
    if (current) dispatch(setReassignAssignee({ id: current.id, name: current.name }))
  }, [dispatch, flagDetail, selectedCrewOption])

  const handleSelectCrew = useCallback(
    (index: number) => dispatch(setReassignAssignee(crewOptions[index] ?? null)),
    [dispatch, crewOptions]
  )
  const handleReasonChange = useCallback((reason: string) => dispatch(setReassignReason(reason)), [dispatch])

  const confirmReassign = () =>
    dispatch(
      showAlert({
        header: t('qaFlags.qaAddendum.reassign.confirmTitle'),
        message: t('qaFlags.qaAddendum.reassign.confirmMessage', { name: selectedCrewOption?.name }),
        buttons: [
          { text: t('qaFlags.qaAddendum.reassign.confirmCancel'), role: 'cancel' },
          { text: t('qaFlags.qaAddendum.reassign.confirmConfirm'), handler: () => dispatch(reassignQaFlag()) },
        ],
      })
    )

  const handleReassignDone = () => {
    if (!canReassignDone || !selectedCrewOption || !flagId) return
    if (isAllCrewSelected) {
      dispatch(reassignQaFlag())
      return
    }
    confirmReassign()
  }

  return (
    <IonPage id='QaAddendumReassignPage'>
      <QaFlagHeader showMenuButton={false} />
      <HeaderNav
        prevLabel={t('common.buttons.Back')}
        isBackButton
        title={t('qaFlags.qaAddendum.reassign.title')}
        prevOnClick={goBack}
      />
      <IonContent fullscreen color='light' className='qa-addendum-flag-resolution-content'>
        <div className={`qa-addendum-shell${isExpanded ? ' qa-addendum-shell-expanded' : ''}`}>
          {isExpanded && (
            <div
              className='qa-addendum-flag-resolution-backdrop'
              onPointerDown={() => setIsExpanded(false)}
              aria-hidden='true'
            />
          )}
          <div className='qa-addendum-scroll'>
            {isDetailLoading || !flagDetail ? (
              <IonSpinner className='procedure-loader' data-testid='QaFlagDetailSpinner' />
            ) : null}
            {flagDetail && !isDetailLoading && (
              <ReassignCrewMemberContent
                crewOptions={crewOptions}
                selectedIndex={selectedCrewIndex}
                onSelectIndex={handleSelectCrew}
              />
            )}
          </div>
          {flagDetail && !isDetailLoading && (
            <QaFlagActionFooter
              textareaLabel={t('qaFlags.qaAddendum.flagResolution.reasonForChange')}
              dataTestId='ReassignCrewReasonForChange'
              value={reasonForChange}
              onChange={handleReasonChange}
              maxLength={4000}
              isExpanded={isExpanded}
              onExpandedChange={setIsExpanded}
              primaryLabel={t('qaFlags.qaAddendum.flagResolution.done')}
              canPrimary={canReassignDone}
              onCancel={goBack}
              onPrimary={handleReassignDone}
              primaryTestId='ReassignCrewDoneButton'
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  )
}

export default ReassignPage
